import AssetService from '@/services/AssetService';
import type { KPICalculationOptions } from '@/types/kpi';
import { useQuery } from '@tanstack/react-query';

export interface UseAssetKpisOptions extends KPICalculationOptions {
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
}

export function useAssetKpis(
  assetId: string,
  options: UseAssetKpisOptions = {},
) {
  const {
    period = 'annual',
    includeVacancy = true,
    includeAppreciation = true,
    enabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutes
    cacheTime = 30 * 60 * 1000, // 30 minutes
    ...calculationOptions
  } = options;

  return useQuery({
    queryKey: [
      'asset-kpis',
      assetId,
      period,
      includeVacancy,
      includeAppreciation,
    ],
    queryFn: () =>
      AssetService.getAssetKpis(assetId, period, calculationOptions),
    enabled: enabled && !!assetId,
    staleTime,
    cacheTime,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

export function useComparativeKpis(
  assetId: string,
  options: UseAssetKpisOptions = {},
) {
  const {
    period = 'annual',
    enabled = true,
    staleTime = 5 * 60 * 1000,
    cacheTime = 30 * 60 * 1000,
    ...calculationOptions
  } = options;

  return useQuery({
    queryKey: ['asset-comparative-kpis', assetId, period],
    queryFn: () =>
      AssetService.getComparativeKpis(assetId, period, calculationOptions),
    enabled: enabled && !!assetId,
    staleTime,
    cacheTime,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

export function usePortfolioKpis(options: UseAssetKpisOptions = {}) {
  const {
    period = 'annual',
    enabled = true,
    staleTime = 5 * 60 * 1000,
    cacheTime = 30 * 60 * 1000,
    ...calculationOptions
  } = options;

  return useQuery({
    queryKey: ['portfolio-kpis', period],
    queryFn: () => AssetService.getPortfolioKpis(period, calculationOptions),
    enabled,
    staleTime,
    cacheTime,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

export function useKpiComparison(
  assetIds: string[],
  options: UseAssetKpisOptions = {},
) {
  const {
    period = 'annual',
    enabled = true,
    staleTime = 5 * 60 * 1000,
    cacheTime = 30 * 60 * 1000,
    ...calculationOptions
  } = options;

  return useQuery({
    queryKey: ['kpi-comparison', assetIds, period],
    queryFn: () =>
      AssetService.getKpiComparison(assetIds, period, calculationOptions),
    enabled: enabled && assetIds.length > 0,
    staleTime,
    cacheTime,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
